// New project or edit one: name, client, rate, billable, export rounding. The color slot is fixed at creation.
import { useState } from 'react';
import { client as findClient, project as findProject } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { Field, Select, Sheet, TextInput, Toggle, onValue } from '../components/common.tsx';
import { ProLock } from '../components/ProLock.tsx';
import { useFmt, useStore } from '../store/hooks.tsx';
import { Button } from '../ui/ds.tsx';

export function ProjectEditor({ projectId, then }: { projectId: string | null; then?: ((projectId: string) => void) | undefined }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const existing = findProject(store.ws, projectId);
  const [name, setName] = useState(existing?.name ?? '');
  const [clientName, setClientName] = useState(findClient(store.ws, existing?.clientId)?.name ?? '');
  const [rate, setRate] = useState(existing?.rateCents != null ? fmt.number(existing.rateCents / 100, existing.rateCents % 100 === 0 ? 0 : 2) : '');
  const [billable, setBillable] = useState(existing?.billable ?? true);
  // Rates, rounding and the default rounding are Pro (spec/pro.md); without it a project keeps what it had.
  const pro = store.pro;
  const [rounding, setRounding] = useState(existing?.roundingMinutes ?? (pro ? Number(store.ws.settings.get('defaultRoundingMinutes') ?? 0) : 0));
  const slot = existing?.colorSlot ?? m.nextColorSlot(store.ws);
  const trimmed = name.trim();
  const taken = store.ws.projects.some((p) => p.deletedAt === null && p.archivedAt === null && p.id !== existing?.id && p.name.toLowerCase() === trimmed.toLowerCase());
  const rateCents = pro ? m.parseAmount(rate) : existing?.rateCents ?? null;
  const valid = trimmed.length > 0 && !taken && rateCents !== undefined;
  const close = () => store.closeSheet();
  const save = () => {
    if (!valid || rateCents === undefined) return;
    const draft = pro ? { name: trimmed, clientName, rateCents, billable, roundingMinutes: rounding }
      : { name: trimmed, clientName, rateCents: existing?.rateCents ?? null, billable, roundingMinutes: existing?.roundingMinutes ?? 0, clientTakesRate: false };
    const { changes, project } = m.saveProject(store.ws, draft, existing, store.clock());
    void store.apply(changes);
    if (!existing && store.ui.draftProjectId === null) store.setUi({ draftProjectId: project.id });
    close();
    then?.(project.id);
  };
  return (
    <Sheet title={<span className="title-dot"><span className="tl-dot" style={{ background: `var(--project-${slot})`, width: 12, height: 12 }} />{existing ? t('Projekt bearbeiten') : t('Neues Projekt')}</span>}
      onClose={close} footer={<>
        <span className="spacer" />
        <Button variant="plain" onClick={close}>{t('Abbrechen')}</Button>
        <Button variant="primary" disabled={!valid} onClick={save}>{existing ? t('Sichern') : t('Anlegen')}</Button>
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <Field label={t('Name')} error={taken ? t('Ein Projekt mit diesem Namen gibt es schon.') : undefined}>
          <TextInput autoFocus value={name} placeholder={t('z. B. Kundenportal Relaunch')} onChange={onValue(setName)} />
        </Field>
        <Field label={t('Kunde')}>
          <TextInput value={clientName} placeholder={t('optional')} list="tally-clients" onChange={onValue(setClientName)} />
          <datalist id="tally-clients">{store.ws.clients.filter((c) => c.deletedAt === null).map((c) => <option key={c.id} value={c.name} />)}</datalist>
        </Field>
        {pro ? (
          <div className="form-row">
            <Field label={t('Stundensatz (€)')} error={rateCents === undefined ? t('Bitte eine Zahl eingeben, z. B. 95 oder 95,50.') : undefined}>
              <TextInput inputMode="decimal" value={rate} placeholder={t('z. B. 95')} onChange={onValue(setRate)} />
            </Field>
            <Field label={t('Rundung im Export')}>
              <Select value={String(rounding)} onChange={(e) => setRounding(Number(e.target.value))}>
                <option value="0">{t('Keine')}</option><option value="1">{t('1 min')}</option><option value="6">{t('6 min')}</option><option value="15">{t('15 min')}</option>
              </Select>
            </Field>
          </div>
        ) : <ProLock text={t('Stundensätze und Rundung gibt es mit Pro.')} onOpen={close} />}
        <Field inline label={t('Abrechenbar')}><Toggle checked={billable} onChange={setBillable} label={t('Abrechenbar')} /></Field>
        <p className="note">{existing ? t('Gerundet wird nur im Export, die App zeigt immer exakte Zeiten.')
          : t('Die Farbe wird beim Anlegen vergeben und bleibt fest. Gerundet wird nur im Export, die App zeigt immer exakte Zeiten.')}</p>
        <p className="note note--faint">{t('Ordner-Regeln (Sessions aus einem Ordner automatisch zuordnen) legst du in der Mac-App an.')}</p>
        <button type="submit" hidden />
      </form>
    </Sheet>
  );
}
